import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  greyColours,
  brandColours,
  brandFonts,
  fontSize,
  standardTransition,
  visuallyHidden,
} from '../styles';
import tripadvisorLogo from '../images/tripadvisor-logo.img.svg';

const StyledSpaFilters = styled.aside``;

const StyledForm = styled.form`
  background-color: ${greyColours.lighterGrey};
  border: 1px solid ${greyColours.lightGrey};
  border-radius: 10px;
`;

const StyledHeader = styled.header`
  padding: 15px 20px;
  border-bottom: 1px solid ${greyColours.lightGrey};

  ${minBreakpointQuery.mlarge`
    padding-right: 25px;
    padding-left: 25px;
  `}

  ${minBreakpointQuery.large`
    padding-right: 30px;
    padding-left: 30px;
  `}
`;

const StyledHeading = styled.h2`
  ${brandFonts.secondary}
  ${fontSize(22)};
`;

const StyledGroups = styled.div`
  padding: 15px 20px;

  ${minBreakpointQuery.mlarge`
    padding-right: 25px;
    padding-left: 25px;
  `}

  ${minBreakpointQuery.large`
    padding-right: 30px;
    padding-left: 30px;
  `}
`;

const StyledGroup = styled.fieldset`
  margin-top: 18px;
  padding-bottom: 22px;
  border-bottom: 1px solid ${greyColours.lightGrey};

  &:first-child {
    margin-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const StyledChoiceGroup = styled.fieldset`
  margin-top: 18px;

  &:first-child {
    margin-top: 0;
  }
`;

const StyledSubeading = styled.legend`
  margin-bottom: 4px;
  ${fontSize(14)};

  ${({ isMinTripadvisorRating }) => {
    if (isMinTripadvisorRating) {
      return css`
        img {
          width: 100px;
        }

        span {
          ${visuallyHidden()};
        }
      `;
    }
  }}
`;

const StyledOption = styled.div`
  margin-top: 8px;
  display: ${({ display }) => (display === false ? 'none' : 'block')};

  &:first-child {
    margin-top: 0;
  }
`;

// moved up for checked styling
const StyledText = styled.label`
  position: relative;
  display: block;
  padding-left: 30px;
  ${fontSize(14)};
  line-height: 1.5;
  cursor: pointer;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 20px;
    width: 20px;
    border: 1px solid ${brandColours.primary};
    transition: ${standardTransition('background-color')};
  }

  &:after {
    content: '';
    position: absolute;
    border: 2px solid ${standardColours.transparent};
    transition: ${standardTransition('background-color')},
      ${standardTransition('border-color')};
  }

  ${({ isMinTripadvisorRating }) => {
    if (isMinTripadvisorRating) {
      return css`
        img {
          width: 60px;
        }

        span {
          ${visuallyHidden()};
        }
      `;
    }
  }}
`;

const StyledInput = styled.input`
  ${visuallyHidden()};

  &[type='checkbox'] {
    + ${StyledText} {
      &:after {
        top: 4px;
        left: 7.5px;
        height: 10px;
        width: 5px;
        border-top: none;
        border-left: none;
        transform: rotate(45deg);
      }

      &:hover {
        &:after {
          border-color: ${brandColours.primary};
        }
      }
    }

    &:checked + ${StyledText} {
      &:before {
        background-color: ${brandColours.primary};
      }

      &:after {
        border-color: ${standardColours.white};
      }
    }
  }

  &[type='radio'] {
    + ${StyledText} {
      &:before {
        border-radius: 50%;
      }

      &:after {
        top: 6px;
        left: 6px;
        height: 8px;
        width: 8px;
        border-radius: 50%;
      }

      &:hover {
        &:after {
          background-color: ${brandColours.primary};
        }
      }
    }

    &:checked + ${StyledText} {
      &:before {
        background-color: ${brandColours.primary};
      }

      &:after {
        background-color: ${standardColours.white};
      }
    }
  }
`;

const SpaFilters = ({
  meta,
  showRadialSearch,
  arePackages,
  handleChange,
  ...props
}) => {
  const {
    spaseekers: { spaFilters, packageFilters },
  } = useStaticQuery(graphql`
    query SpaFiltersQuery {
      spaseekers {
        spaFilters {
          ...SpaFiltersFragment
        }
        packageFilters {
          ...SpaFiltersFragment
        }
      }
    }
  `);

  const metadata = meta && meta.metadata;

  const staticFilterGroups = [
    {
      name: 'Distance from Location',
      filters: [
        {
          field: 'radius',
          name: 'Distance from Location',
          type: 'CHOICE',
          values: [
            {
              description: '5 Miles',
              value: 5,
            },
            {
              description: '10 Miles',
              value: 10,
            },
            {
              description: '25 Miles',
              value: 25,
            },
            {
              description: '50 Miles',
              value: 50,
            },
          ],
        },
      ],
    },
    {
      name: 'Customer Rating',
      filters: [
        {
          field: 'minGuestRating',
          name: 'Customer Rating',
          type: 'CHOICE',
          values: [
            {
              description: '5',
              value: 5,
            },
            {
              description: '4 or more',
              value: 4,
            },
            {
              description: '3 or more',
              value: 3,
            },
            {
              description: '2 or more',
              value: 2,
            },
            {
              description: '1 or more',
              value: 1,
            },
          ],
        },
      ],
    },
    {
      name: 'Tripadvisor Rating',
      filters: [
        {
          field: 'minTripAdvisorRating',
          name: 'Tripadvisor Rating',
          type: 'CHOICE',
          values: [
            {
              description: '5',
              value: 5,
            },
            {
              description: '4 or more',
              value: 4,
            },
            {
              description: '3 or more',
              value: 3,
            },
            {
              description: '2 or more',
              value: 2,
            },
            {
              description: '1 or more',
              value: 1,
            },
          ],
        },
      ],
    },
  ];

  const dynamicFilterGroups = arePackages ? packageFilters : spaFilters;

  const filterGroups = [
    ...staticFilterGroups.slice(showRadialSearch ? 0 : 1),
    ...dynamicFilterGroups,
  ];

  const showFilter = (key, value, type) => {
    if (metadata) {
      const row = metadata[key];
      if (!!row) {
        const count = row[value];
        return (
          !isNaN(count) && (count > 0 || (type === 'CHOICE' && value === 'any'))
        );
      }
    }

    // Handle where the facets might not be loaded, fallback to default behaviour
    return metadata;
  };

  const getFacetCount = (key, value) => {
    if (metadata) {
      const row = metadata[key];
      if (!!row) {
        const count = row[value];
        if (!isNaN(count)) {
          return `(${count})`;
        }
        return `(0)`;
      }
    }

    return '';
  };

  return (
    <StyledSpaFilters {...props}>
      <StyledForm name="spa-filters" onChange={e => handleChange(e)}>
        <StyledHeader>
          <StyledHeading>Filter By:</StyledHeading>
        </StyledHeader>
        <StyledGroups>
          {filterGroups.map(({ name, filters }, id) => (
            <StyledGroup key={id}>
              {filters[0].type === 'FLAG' && (
                <StyledSubeading>{name}</StyledSubeading>
              )}
              {filters.map(({ type, name, field, values }, id) => {
                const isMinTripadvisorRating = field === 'minTripAdvisorRating';
                const key =
                  field === 'radius'
                    ? 'distance'
                    : field === 'minGuestRating'
                      ? 'rating'
                      : isMinTripadvisorRating
                        ? 'tripAdvisorRating'
                        : field;

                return (
                  <React.Fragment key={id}>
                    {type === 'FLAG' && (
                      <StyledOption display={showFilter(field, 1, type)}>
                        <StyledInput type="checkbox" id={field} name={field} />
                        <StyledText for={field}>
                          {name} {getFacetCount(field, 1)}
                        </StyledText>
                      </StyledOption>
                    )}
                    {type === 'CHOICE' && (
                      <StyledChoiceGroup>
                        <StyledSubeading
                          isMinTripadvisorRating={isMinTripadvisorRating}
                        >
                          {isMinTripadvisorRating && (
                            <img
                              src={tripadvisorLogo}
                              alt="Tripadvisor logo"
                              loading="lazy"
                            />
                          )}
                          <span>{name}</span>
                        </StyledSubeading>
                        <StyledOption>
                          <StyledInput
                            type="radio"
                            id={`${field}-any`}
                            name={field}
                            value="any"
                          />
                          <StyledText for={`${field}-any`}>Any</StyledText>
                        </StyledOption>
                        {values.map(({ description, value }, id) => (
                          <StyledOption
                            key={id}
                            display={showFilter(key, value, type)}
                          >
                            <StyledInput
                              type="radio"
                              id={`${field}-${value}`}
                              name={field}
                              value={value}
                            />
                            <StyledText
                              for={`${field}-${value}`}
                              isMinTripadvisorRating={isMinTripadvisorRating}
                            >
                              {isMinTripadvisorRating && (
                                <img
                                  src={`https://static.tacdn.com/img2/ratings/traveler/ss${value}.0.svg`}
                                  alt={value}
                                  loading="lazy"
                                />
                              )}
                              <span>{description}</span>{' '}
                              {getFacetCount(key, value)}
                            </StyledText>
                          </StyledOption>
                        ))}
                      </StyledChoiceGroup>
                    )}
                  </React.Fragment>
                );
              })}
            </StyledGroup>
          ))}
        </StyledGroups>
      </StyledForm>
    </StyledSpaFilters>
  );
};

export default SpaFilters;

export const SpaFiltersFragment = graphql`
  fragment SpaFiltersFragment on SPASEEKERS_FilterGroup {
    name
    filters {
      name
      field
      type
      values {
        description
        value
      }
    }
  }
`;
